'use client';

import React, { useEffect, useRef } from 'react';

interface LoanType {
  id: string;
  icon: React.ReactNode;
  name: string;
  description: string;
  badge: string;
  badgeColor: string;
}

const LOAN_TYPES: LoanType[] = [
  {
    id: 'short-term',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6l4 2m6-2a10 10 0 11-20 0 10 10 0 0120 0z" />
      </svg>
    ),
    name: 'Short-Term Loan',
    description: 'Fast capital for immediate business needs — inventory, payroll gaps, or urgent repairs. Repayment periods of 3–18 months with quick approval timelines.',
    badge: '3–18 Months',
    badgeColor: 'bg-sky-light text-sky-dark border-sky/30',
  },
  {
    id: 'long-term',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
    name: 'Long-Term Loan',
    description: 'Structured financing for major investments — equipment, real estate, or business expansion. Terms from 2–25 years with predictable monthly payments.',
    badge: '2–25 Years',
    badgeColor: 'bg-sage-light text-sage-dark border-sage/30',
  },
  {
    id: 'line-of-credit',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
      </svg>
    ),
    name: 'Line of Credit',
    description: 'Revolving access to funds you draw as needed. Only pay interest on what you use — ideal for managing cash flow fluctuations and seasonal demand.',
    badge: 'Revolving',
    badgeColor: 'bg-amber-50 text-amber-700 border-amber-200',
  },
  {
    id: 'sba-loan',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9" />
      </svg>
    ),
    name: 'SBA Loan',
    description: 'Government-backed financing with competitive rates and longer repayment terms. SBA 7(a) and 504 programs for qualified small businesses seeking up to $5M.',
    badge: 'Gov. Backed',
    badgeColor: 'bg-sky-pale text-slate border-slate-mist/60',
  },
  {
    id: 'mca',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
      </svg>
    ),
    name: 'Merchant Cash Advance',
    description: 'Revenue-based financing repaid as a percentage of daily card sales. No fixed monthly payment — repayment scales with your business performance.',
    badge: 'Revenue-Based',
    badgeColor: 'bg-purple-50 text-purple-700 border-purple-200',
  },
  {
    id: 'invoice',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    ),
    name: 'Invoice Financing',
    description: 'Unlock cash tied up in unpaid invoices. Advance up to 90% of outstanding receivables immediately — stop waiting 30–90 days for clients to pay.',
    badge: 'Up to 90%',
    badgeColor: 'bg-teal-50 text-teal-700 border-teal-200',
  },
];

export default function BusinessLoanTypes() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('active');
        });
      },
      { threshold: 0.08, rootMargin: '0px 0px -40px 0px' }
    );
    sectionRef.current?.querySelectorAll('.reveal').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section id="business-loans" className="py-24 lg:py-32 bg-white overflow-hidden" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="max-w-2xl mb-16 reveal">
          <span className="inline-block px-4 py-1.5 rounded-full bg-sky-light border border-sky/20 text-xs font-semibold text-sky-dark font-body uppercase tracking-wide mb-5">
            Business Financing
          </span>
          <h2 className="font-heading font-bold text-display text-slate mb-4">
            Business Loan Types
          </h2>
          <p className="font-body text-slate-fog text-base leading-relaxed">
            Every business has different capital needs. We match you with the right structure — whether you need fast cash today or long-term growth financing.
          </p>
        </div>

        {/* 3-column grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {LOAN_TYPES.map((loan, idx) => (
            <div
              key={loan.id}
              className="reveal group bg-cloud rounded-4xl p-7 border border-slate-mist/40 hover:border-sky/30 hover:shadow-card transition-all duration-300 flex flex-col"
              style={{ transitionDelay: `${idx * 80}ms` }}
            >
              {/* Icon */}
              <div className="w-12 h-12 rounded-2xl bg-white border border-slate-mist/40 flex items-center justify-center text-sky mb-5 group-hover:bg-sky-light group-hover:border-sky/30 transition-all duration-300 shadow-sm">
                {loan.icon}
              </div>

              {/* Name + Badge */}
              <div className="flex items-start justify-between gap-3 mb-3">
                <h3 className="font-heading font-bold text-lg text-slate leading-tight">
                  {loan.name}
                </h3>
                <span className={`shrink-0 inline-block px-2.5 py-1 rounded-full text-xs font-semibold font-body border ${loan.badgeColor}`}>
                  {loan.badge}
                </span>
              </div>

              {/* Description */}
              <p className="font-body text-sm text-slate-fog leading-relaxed flex-1">
                {loan.description}
              </p>

              {/* Bottom CTA */}
              <div className="mt-5 pt-4 border-t border-slate-mist/30">
                <a
                  href="/loans/new"
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-sky hover:text-sky-dark font-body transition-colors group/link"
                >
                  Apply for this loan
                  <svg className="w-3.5 h-3.5 group-hover/link:translate-x-0.5 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                  </svg>
                </a>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
